import { z } from "zod";

const id = z
  .string()
  .min(1)
  .max(200)
  .refine((v) => v.trim() === v);
export const setupEnvelopeSchema = z
  .object({
    flowVersion: z.literal(1),
    tenantId: id,
    revision: z.number().int().positive(),
    displayName: z.string().trim().max(200),
    firstSeenAt: z.string().datetime(),
    completedAt: z.string().datetime().optional(),
    skipped: z
      .array(id)
      .max(100)
      .refine((v) => new Set(v).size === v.length),
  })
  .strict();
export type SetupEnvelope = z.infer<typeof setupEnvelopeSchema>;
export interface SetupRequirementProvider {
  id: string;
  title: string;
  feature: string;
  route: string;
  required: boolean;
  dependsOn?: string[];
  evaluate(
    tenantId: string,
    envelope: Readonly<SetupEnvelope>,
  ): Promise<{ ready: boolean; code: string }>;
}
export interface SetupView {
  envelope: SetupEnvelope;
  steps: {
    id: string;
    title: string;
    feature: string;
    route: string;
    required: boolean;
    state: "ready" | "missing" | "skipped";
    code: string;
  }[];
  ready: boolean;
  needsInitialSetup: boolean;
  warning: boolean;
}
export class SetupError extends Error {
  constructor(
    readonly code: string,
    readonly status = 400,
  ) {
    super(code);
  }
}
export interface SetupStore {
  get(tenant: string): Promise<SetupEnvelope | undefined>;
  save(value: SetupEnvelope, expectedRevision: number): Promise<SetupEnvelope>;
}
export class MemorySetupStore implements SetupStore {
  protected records: SetupEnvelope[] = [];
  private writes: Promise<void> = Promise.resolve();
  constructor(initial: unknown = []) {
    this.records = z.array(setupEnvelopeSchema).parse(initial);
    if (
      new Set(this.records.map((r) => r.tenantId)).size !== this.records.length
    )
      throw new SetupError("SETUP_DUPLICATE_TENANT");
  }
  async get(tenant: string) {
    await this.writes;
    return structuredClone(this.records.find((r) => r.tenantId === tenant));
  }
  protected async persist(_records: SetupEnvelope[]) {}
  async save(raw: SetupEnvelope, expectedRevision: number) {
    const value = setupEnvelopeSchema.parse(raw);
    const operation = this.writes
      .catch(() => {})
      .then(async () => {
        const prior = this.records.find((r) => r.tenantId === value.tenantId);
        if (
          (prior?.revision || 0) !== expectedRevision ||
          value.revision !== expectedRevision + 1
        )
          throw new SetupError("SETUP_STALE", 409);
        const next = [
          ...this.records.filter((r) => r.tenantId !== value.tenantId),
          value,
        ];
        await this.persist(structuredClone(next));
        this.records = next;
      });
    this.writes = operation.catch(() => {});
    await operation;
    return structuredClone(value);
  }
}
export const setupChangeSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("details"),
      expectedRevision: z.number().int().positive(),
      displayName: z.string().trim().min(1).max(200),
    })
    .strict(),
  z
    .object({
      action: z.literal("skip"),
      expectedRevision: z.number().int().positive(),
      stepId: id,
      skipped: z.boolean(),
    })
    .strict(),
  z
    .object({
      action: z.literal("complete"),
      expectedRevision: z.number().int().positive(),
    })
    .strict(),
]);
export type SetupChange = z.infer<typeof setupChangeSchema>;
export class TenantSetupService {
  readonly providers: readonly SetupRequirementProvider[];
  constructor(
    readonly store: SetupStore,
    providers: SetupRequirementProvider[],
    private readonly completedDemo = false,
  ) {
    if (providers.length > 100) throw new SetupError("SETUP_PROVIDER_LIMIT");
    const pending = [...providers],
      sorted: SetupRequirementProvider[] = [],
      seen = new Set<string>();
    for (const p of providers) {
      id.parse(p.id);
      if (
        seen.has(p.id) ||
        !p.route.startsWith("/") ||
        p.route.startsWith("//")
      )
        throw new SetupError("SETUP_PROVIDER_INVALID");
      seen.add(p.id);
    }
    while (pending.length) {
      const i = pending.findIndex((p) =>
        (p.dependsOn || []).every((d) => sorted.some((v) => v.id === d)),
      );
      if (i < 0) throw new SetupError("SETUP_DEPENDENCY_INVALID");
      sorted.push(pending.splice(i, 1)[0]);
    }
    this.providers = sorted;
  }
  private async envelope(tenant: string) {
    id.parse(tenant);
    const prior = await this.store.get(tenant);
    if (prior) {
      const parsed = setupEnvelopeSchema.parse(prior);
      if (parsed.tenantId !== tenant)
        throw new SetupError("SETUP_TENANT_MISMATCH");
      return parsed;
    }
    const at = new Date().toISOString();
    const initial: SetupEnvelope = {
      flowVersion: 1,
      tenantId: tenant,
      revision: 1,
      displayName: this.completedDemo && tenant === "demo" ? "Fern & Form" : "",
      firstSeenAt: at,
      skipped: [],
      ...(this.completedDemo && tenant === "demo" ? { completedAt: at } : {}),
    };
    try {
      return await this.store.save(initial, 0);
    } catch (e) {
      if (!(e instanceof SetupError) || e.code !== "SETUP_STALE") throw e;
      const latest = await this.store.get(tenant);
      if (!latest) throw e;
      const parsed = setupEnvelopeSchema.parse(latest);
      if (parsed.tenantId !== tenant)
        throw new SetupError("SETUP_TENANT_MISMATCH");
      return parsed;
    }
  }
  private async evaluate(envelope: SetupEnvelope): Promise<SetupView> {
    const steps: SetupView["steps"] = [];
    for (const p of this.providers) {
      let state: SetupView["steps"][number]["state"] = "missing",
        code = "SETUP_NOT_READY";
      if (!p.required && envelope.skipped.includes(p.id)) {
        state = "skipped";
        code = "SETUP_SKIPPED";
      } else if (
        (p.dependsOn || []).some(
          (d) => steps.find((s) => s.id === d)?.state === "missing",
        )
      )
        code = "SETUP_DEPENDENCY_NOT_READY";
      else {
        try {
          const result = await p.evaluate(
            envelope.tenantId,
            structuredClone(envelope),
          );
          state = result.ready ? "ready" : "missing";
          code = /^[A-Z0-9_]{1,100}$/.test(result.code)
            ? result.code
            : "SETUP_CHECK_FAILED";
        } catch {
          code = "SETUP_CHECK_FAILED";
        }
      }
      steps.push({
        id: p.id,
        title: p.title,
        feature: p.feature,
        route: p.route,
        required: p.required,
        state,
        code,
      });
    }
    const ready = steps.every((s) => !s.required || s.state === "ready");
    return {
      envelope: structuredClone(envelope),
      steps,
      ready,
      needsInitialSetup: !envelope.completedAt,
      warning: !!envelope.completedAt && !ready,
    };
  }
  async get(tenant: string) {
    return this.evaluate(await this.envelope(tenant));
  }
  async change(tenant: string, raw: unknown) {
    const change = setupChangeSchema.parse(raw),
      prior = await this.envelope(tenant);
    if (prior.revision !== change.expectedRevision)
      throw new SetupError("SETUP_STALE", 409);
    const next = structuredClone(prior);
    if (change.action === "details") next.displayName = change.displayName;
    else if (change.action === "skip") {
      const provider = this.providers.find((p) => p.id === change.stepId);
      if (!provider || provider.required)
        throw new SetupError("SETUP_STEP_NOT_SKIPPABLE");
      next.skipped = next.skipped.filter((s) => s !== change.stepId);
      if (change.skipped) next.skipped.push(change.stepId);
      next.skipped.sort();
    } else {
      if (!(await this.evaluate(prior)).ready)
        throw new SetupError("SETUP_REQUIRED_STEPS_MISSING", 409);
      next.completedAt ||= new Date().toISOString();
    }
    next.revision++;
    const saved = await this.store.save(next, prior.revision);
    return this.evaluate(saved);
  }
}
